import { computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { BuyTokensDialogComponent } from '@features/token/components/buy-tokens-dialog/buy-tokens-dialog.component';
import { SubscriptionPlansDialogComponent } from '@features/token/components/subscription-plans-dialog/subscription-plans-dialog.component';
import { DialogService } from '@ngneat/dialog';
import { signalStore, withComputed, withMethods, withProps } from '@ngrx/signals';
import { filter, lastValueFrom, take } from 'rxjs';

import { isDefined } from '../../../utils/is-defined.utils';
import { AuthStore, SubscriptionPlanType, TSubscriptionPlanType } from '../../data-access-authentication';
import {
  CreateCheckoutRequest,
  PaymentData,
  PaymentMetadata,
  PaymentScope,
  Product,
  TPaymentScope,
} from '../../data-access-payment';
import { PaymentService } from '../../data-access-payment/service/payment.service';

export const TokenStore = signalStore(
  withDevtools('token'),
  withComputed((_, authStore = inject(AuthStore)) => ({
    subscription: computed(() => authStore.data()?.organization?.subscription),
    subscriptionPlan: computed(() => authStore.data()?.organization?.subscription?.subscriptionPlanType),
    tokens: computed(() => authStore.data()?.organization?.subscription?.subscriptionCredits),
    totalTokens: computed(() => authStore.data()?.organization?.subscription?.subscriptionTotalAvailableCredits),
    customerId: computed(() => authStore.data()?.customerPaymentId),
    isOrganizationOwner: computed(() => authStore.data()?.organizationOwner),
    canManageSubscription: computed(() => authStore.canManageSubscription()),
    isSubscriptionPlanRequired: computed(() => {
      const subscriptionType = authStore.data()?.organization?.subscription?.subscriptionPlanType;
      const trialPlans: TSubscriptionPlanType[] = [SubscriptionPlanType.TRIAL_END];

      if (!subscriptionType) {
        return true;
      }

      return trialPlans.includes(subscriptionType);
    }),
    isTrial: computed(() => {
      const subscriptionType = authStore.data()?.organization?.subscription?.subscriptionPlanType;
      const trialPlans: TSubscriptionPlanType[] = [SubscriptionPlanType.TRIAL, SubscriptionPlanType.TRIAL_END];

      if (!subscriptionType) {
        return true;
      }

      return trialPlans.includes(subscriptionType);
    }),
    isPremium: computed(() => {
      const subscriptionType = authStore.data()?.organization?.subscription?.subscriptionPlanType;
      const premiumPlans: TSubscriptionPlanType[] = [SubscriptionPlanType.PREMIUM];

      if (!subscriptionType) {
        return true;
      }

      return premiumPlans.includes(subscriptionType);
    }),
  })),
  withProps(({ isSubscriptionPlanRequired }) => ({
    isSubscriptionPlanRequired$: toObservable(isSubscriptionPlanRequired),
  })),
  withMethods(
    (
      store,
      dialogService = inject(DialogService),
      paymentService = inject(PaymentService),
      router = inject(Router),
      authStore = inject(AuthStore),
    ) => {
      async function openBuyTokensDialog(completed?: boolean, tokens?: string) {
        const product = signal<Product | undefined>(undefined);
        const paymentData = signal<PaymentData | undefined>(undefined);

        if (!completed) {
          const [paymentInfo, products] = await Promise.all([
            lastValueFrom(paymentService.getUserPaymentData()),
            lastValueFrom(paymentService.getProducts('TOP_UP')),
          ]);

          product.set(products[0]);
          paymentData.set(paymentInfo);
        }

        dialogService
          .open(BuyTokensDialogComponent, {
            data: {
              subscriptionPlan: store.subscriptionPlan,
              completed,
              tokens,
              product,
              paymentData,
            },
            windowClass: 'buy-tokens-dialog',
            maxWidth: '896px',
          })
          .afterClosed$.pipe(filter(isDefined), take(1))
          .subscribe((data) => {
            const { amount, priceId, portal } = data as { amount?: number; priceId?: string; portal?: boolean };

            if (isDefined(portal)) {
              createPortalSession();
            }

            if (isDefined(amount) && isDefined(priceId)) {
              createCheckoutSession({
                checkoutItems: [{ priceId, quantity: amount }],
                scope: PaymentScope.TOP_UP,
                metadata: {
                  [PaymentMetadata.TOKENS]: `${amount}`,
                },
              });
            }
          });
      }

      async function openSubscriptionPlansDialog() {
        const [products, cyberExposureProducts] = await Promise.all([
          lastValueFrom(paymentService.getProducts('SUBSCRIPTION')),
          lastValueFrom(paymentService.getProducts('CYBER_EXPOSURE_REPORT')),
        ]);

        dialogService
          .open(SubscriptionPlansDialogComponent, {
            data: {
              products,
              cyberExposureProducts,
            },
            maxWidth: store.canManageSubscription() ? '1035px' : '600px',
            width: 'fit-content',
            enableClose: !authStore.isFinance(),
          })
          .afterClosed$.pipe(take(1))
          .subscribe(() => {
            if (!authStore.isFinance()) {
              router.navigate(['/dashboard']);
            }
          });
      }

      async function createCheckoutSession(
        payload: Omit<CreateCheckoutRequest, 'successUrl' | 'cancelUrl' | 'customerId'> & { scope: TPaymentScope },
      ) {
        const { checkoutItems, scope, metadata } = payload;
        const checkout = await lastValueFrom(
          paymentService.createCheckout({
            successUrl: paymentService.createSuccessUrl(scope, metadata),
            cancelUrl: paymentService.createCancelUrl(),
            customerId: store.customerId() || '',
            checkoutItems,
          }),
        );

        if (checkout.url) {
          location.href = checkout.url;
        }
      }

      async function createPortalSession() {
        const portal = await lastValueFrom(
          paymentService.createPortalSession(store.customerId() || '', paymentService.createReturnFromPortalUrl()),
        );

        if (portal.url) {
          location.href = portal.url;
        }
      }

      return { openBuyTokensDialog, openSubscriptionPlansDialog, createCheckoutSession, createPortalSession };
    },
  ),
);
